// AiSidebar (DESIGN §3.3.2): Kopf mit Monogramm, „Bar“, Badge „AI“ und Funkeln; Pill „New chat“,
// Suchfeld, Einträge (Knowledge, Sharing, Sources, Connect), Gesprächsliste als Slot, Fuß mit
// Avatar-Zeile. Eingeklappt 64: nur Icons mit Tooltips.
import { MagnifyingGlass, PencilSimpleLine, SidebarSimple, Sparkle } from '@phosphor-icons/react';
import { type ReactNode } from 'react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';
import { Kbd } from '../kern/Misc';
import { Monogram, PlatformMark } from '../kern/Monogram';
import { type AdminNavItem } from './AdminNav';
import { CountBadge, ShellIconButton, useRovingFocus } from './parts';

export interface AiSidebarItem extends AdminNavItem {
  /** Punkt statt Zahl (Connector-Fehler `danger`, Token läuft ab `warning`). */
  dot?: 'danger' | 'warning';
}

export interface AiSidebarProps {
  items: readonly AiSidebarItem[];
  newChatHref: string;
  /** Live-Filter der Gesprächsliste. */
  search?: string;
  onSearchChange?: (value: string) => void;
  /** Enter im Suchfeld (→ /ai/chats?q=). */
  onSearchSubmit?: (value: string) => void;
  /** Gesprächsliste (ConversationList). */
  conversations?: ReactNode;
  /** Avatar-Zeile mit AccountMenu (bzw. eingeklappt nur der Avatar). */
  footer?: ReactNode;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Nach einer Wahl (Drawer schließen). */
  onNavigate?: () => void;
  homeHref?: string;
  appName?: string;
  className?: string;
}

function ItemLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: AiSidebarItem;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const Glyph = item.icon;
  const dot = item.dot ? (
    <span
      aria-hidden
      className={cn(
        'size-2 shrink-0 rounded-full',
        item.dot === 'danger' ? 'bg-danger' : 'bg-warning',
      )}
    />
  ) : null;
  if (collapsed) {
    return (
      <span className="relative inline-flex">
        <ShellIconButton
          icon={Glyph}
          label={item.label}
          href={item.href}
          reload={item.reload}
          tone="nav"
          weight={item.active ? 'fill' : 'regular'}
          className={item.active ? 'bg-card text-accent-fg' : undefined}
        />
        {item.dot ? (
          <span
            aria-hidden
            className={cn(
              'absolute top-1.5 right-1.5 size-2 rounded-full ring-2 ring-page',
              item.dot === 'danger' ? 'bg-danger' : 'bg-warning',
            )}
          />
        ) : null}
      </span>
    );
  }
  return (
    <RouterLink
      href={item.href}
      reload={item.reload}
      data-roving
      aria-current={item.active ? 'page' : undefined}
      onClick={onNavigate}
      className={cn(
        'flex h-9 items-center gap-3 rounded-md px-2.5 text-label font-medium transition-colors duration-(--bar-dur-fast)',
        item.active
          ? 'bg-card text-accent-fg'
          : 'text-fg-secondary hover:bg-card-hover hover:text-fg',
      )}
    >
      <Glyph size={18} weight={item.active ? 'fill' : 'regular'} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.count ? (
        <CountBadge count={item.count} tone={item.countTone ?? 'warning'} cap={false} />
      ) : (
        dot
      )}
    </RouterLink>
  );
}

export function AiSidebar({
  items,
  newChatHref,
  search = '',
  onSearchChange,
  onSearchSubmit,
  conversations,
  footer,
  collapsed,
  onToggleCollapsed,
  onNavigate,
  homeHref = '/',
  appName = 'Bar',
  className,
}: AiSidebarProps) {
  const roving = useRovingFocus<HTMLElement>();
  if (collapsed) {
    return (
      <div
        className={cn(
          'flex h-full w-ai-sidebar-collapsed flex-col items-center gap-2 border-r border-line-subtle bg-page py-3',
          className,
        )}
      >
        <RouterLink href={homeHref} aria-label={`${appName} AI home`} className="mb-1 rounded-md">
          <Monogram size={28} appName={appName} platform="ai" />
        </RouterLink>
        {onToggleCollapsed ? (
          <ShellIconButton
            icon={SidebarSimple}
            label="Expand sidebar"
            onClick={onToggleCollapsed}
          />
        ) : null}
        <ShellIconButton icon={PencilSimpleLine} label="New chat" href={newChatHref} />
        {onToggleCollapsed ? (
          <ShellIconButton
            icon={MagnifyingGlass}
            label="Search chats"
            onClick={onToggleCollapsed}
          />
        ) : null}
        <nav aria-label="Bar AI" className="flex flex-col items-center gap-1">
          {items.map((item) => (
            <ItemLink key={item.id} item={item} collapsed />
          ))}
        </nav>
        <div className="mt-auto">{footer}</div>
      </div>
    );
  }
  return (
    <div
      data-ai-sidebar
      className={cn(
        'relative flex h-full w-ai-sidebar min-w-0 flex-col border-r border-line-subtle bg-page',
        className,
      )}
    >
      <div className="relative flex h-14 shrink-0 items-center gap-2 px-3">
        <RouterLink
          href={homeHref}
          aria-label={`${appName} AI home`}
          className="min-w-0 flex-1 rounded-md"
          onClick={onNavigate}
        >
          <span className="inline-flex items-center gap-1.5">
            <PlatformMark platform="ai" appName={appName} size={28} />
            <Sparkle size={16} weight="fill" aria-hidden className="text-ai" />
          </span>
        </RouterLink>
        {onToggleCollapsed ? (
          <ShellIconButton
            icon={SidebarSimple}
            label="Collapse sidebar"
            size="sm"
            onClick={onToggleCollapsed}
          />
        ) : null}
      </div>
      <div className="relative flex shrink-0 flex-col gap-2 px-3 pb-2">
        <RouterLink
          href={newChatHref}
          onClick={onNavigate}
          className="flex h-10 items-center gap-2 rounded-full bg-pill-page px-4 text-button text-fg transition-colors duration-(--bar-dur-fast) hover:bg-pill-page-hover"
        >
          <PencilSimpleLine size={18} aria-hidden />
          <span className="flex-1">New chat</span>
          <Kbd className="hidden lg:inline-flex">⌘⇧O</Kbd>
        </RouterLink>
        {onSearchChange ? (
          <label className="flex h-9 items-center gap-2 rounded-lg bg-input px-3 focus-within:outline-2 focus-within:outline-offset-0 focus-within:outline-focus focus-within:outline-solid light:ring-1 light:ring-line">
            <MagnifyingGlass size={16} aria-hidden className="shrink-0 text-fg-secondary" />
            <span className="sr-only">Search chats</span>
            <input
              type="search"
              value={search}
              placeholder="Search chats"
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearchSubmit?.(search)}
              className="h-full min-w-0 flex-1 bg-transparent text-label text-fg outline-none placeholder:text-fg-placeholder"
            />
          </label>
        ) : null}
        <nav ref={roving} aria-label="Bar AI" className="flex flex-col gap-0.5 pt-1">
          {items.map((item) => (
            <ItemLink key={item.id} item={item} onNavigate={onNavigate} />
          ))}
        </nav>
      </div>
      <div className="relative flex min-h-0 flex-1 flex-col px-2">{conversations}</div>
      {footer ? (
        <div className="relative shrink-0 border-t border-line-subtle p-2">{footer}</div>
      ) : null}
    </div>
  );
}
